/**
 * #1006 / #1226 — the Tracker's Edit (a US order) and Close (a US holding):
 * the US ticket on the exact account visit the row was read under. Edit is
 * the #1017 edit mode (check, cancel, confirm, place once; unconfirmed offers
 * Check status only). Close is a locked Sell of the holding, preset like
 * international Polymarket's Close and sent only by the trader's one Place
 * Order click. While a cancel or submit is in flight, or an edit's
 * replacement is still being checked or sent, the window cannot be dismissed,
 * so its outcome is never dropped; afterwards closing it sends nothing and
 * the row's account re-reads.
 */
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { Modal } from '@/components/atoms/Modal';
import type { UsAccountContext } from '@/lib/polymarketUs/accountClient';
import type { UsCloseEvent, UsOrderRow, UsPositionRow } from '@/lib/polymarketUs/types';
import { ConnectedUsTicket } from '../PolymarketUs/ticket/ConnectedUsTicket';
import { PortfolioVenueBadge } from '../PolymarketUs/usVenueLabel';

/** One ticket action, bound to the exact account visit it was opened under: a
 * new visit (a refresh of access) drops it unmounted. `accountLabel` names the
 * account only where it can differ (two or more accounts); null or absent otherwise. */
export type UsActionMount = {
  context: UsAccountContext;
  /** Refresh local reminders after submit settles, including a lost response.
   * This never repeats the submit or starts a status lookup. */
  onRequestRecorded(): void;
  accountLabel?: string | null;
  /** The host closed the ticket; the account re-reads. */
  onDone(): void;
} & (
  /** `onEditReplaced`: the replacement's intent, once sent (see UsManualTicket). */
  | { kind: 'edit'; order: UsOrderRow; onEditReplaced?(intentId: string): void }
  /** Close: sell `quantity` (the holding's established G1 size) of `position`.
   * `onCloseEvent`: the ticket's close lifecycle, which drives the guard that
   * keeps a second Close off this holding ("closing must not double-sell"). */
  | { kind: 'close'; position: UsPositionRow; quantity: string; onCloseEvent(event: UsCloseEvent): void }
);

export function UsActionWindow({ mount }: { mount: UsActionMount }) {
  const { context, onRequestRecorded, onDone } = mount;
  const [inFlight, setInFlight] = useState(false);
  const committed = useRef(false);
  const updateCommitted = useCallback((next: boolean) => {
    committed.current = next;
    setInFlight(next);
  }, []);
  // The DOM and Escape listener can still describe the previous render when
  // submit starts. The ticket publishes this ref before calling its client.
  const close = () => { if (!committed.current) onDone(); };
  const target = mount.kind === 'edit' ? mount.order.target : mount.position.target;
  return (
    <UsActionWindowView kind={mount.kind} target={target} accountLabel={mount.accountLabel} inFlight={inFlight} onClose={close}>
      {mount.kind === 'edit' ? (
        <div data-testid="tracker-us-edit">
          <ConnectedUsTicket context={context} editOrder={mount.order} onRequestRecorded={onRequestRecorded} layout="rail"
            onEditReplaced={mount.onEditReplaced} onCommittedChange={updateCommitted} />
        </div>
      ) : (
        <div data-testid="tracker-us-close">
          <ConnectedUsTicket context={context} onRequestRecorded={onRequestRecorded} layout="rail"
            lockedTarget={{ marketSlug: mount.position.target.marketSlug, outcome: mount.position.target.outcome }}
            closeHolding={{ quantity: mount.quantity }} onCloseSent={mount.onCloseEvent} onCommittedChange={updateCommitted} />
        </div>
      )}
    </UsActionWindowView>
  );
}

/** The action window's frame: the Tracker's own titles ("Edit order",
 * "Close position", with the venue badge), the exact market and outcome, the
 * account only where it can differ, the ticket (children), and the in-flight
 * line. While `inFlight` it cannot be dismissed. Exported for the Layout Lab
 * (#1113), which renders it in both states around a lab ticket. */
export function UsActionWindowView({ kind, target, accountLabel = null, inFlight, onClose, children }: {
  kind: 'edit' | 'close';
  target: { marketSlug: string; outcome: string; label?: string };
  /** The account's name where it can differ (two or more accounts); null = one account. */
  accountLabel?: string | null;
  inFlight: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <Modal isOpen onClose={onClose} title={kind === 'edit' ? 'Edit order' : 'Close position'}
      size="md" closeOnBackdrop={false} closeOnEsc={!inFlight} showClose={!inFlight}>
      <div className="mb-2 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs" data-testid="tracker-us-action-identity">
        <PortfolioVenueBadge venue="polymarket_us" />
        {/* The label already names the outcome ("… — YES"); the slug does not. */}
        <p className="min-w-0 break-words font-medium [overflow-wrap:anywhere]" title={target.marketSlug}>
          {target.label ?? `${target.marketSlug} · ${target.outcome.toUpperCase()}`}
        </p>
        {accountLabel && <p className="text-muted-foreground">{accountLabel}</p>}
      </div>
      {children}
      {/* #1113: why Esc and ✕ stop working while a request is out. Its two
          lines are always reserved, so it appears and clears moving nothing;
          outside the identity block and the ticket. */}
      <p className="text-muted-foreground mt-2 min-h-[2lh] text-xs" role="status" data-testid="tracker-us-action-waiting">
        {inFlight ? 'Waiting for Polymarket US to answer; this window can close once the outcome shows.' : ''}
      </p>
    </Modal>
  );
}
